/* Offline mutation queue — IndexedDB-backed.
 *
 * Stores pending non-GET API calls when navigator.onLine===false.
 * Drains on `online` event or manual sync. Used by lib/api.js axios interceptor.
 */
const DB_NAME = "gioagent-offline";
const DB_VERSION = 1;
const STORE = "pending";
const sensitive = (url) => /\/auth(?:\/|$)/.test(url || "") || /\/companion-bookings(?:\/|$)/.test(url || "");

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const s = db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
        s.createIndex("ts", "ts");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    let result;
    Promise.resolve(fn(store)).then((r) => { result = r; });
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

export async function enqueueMutation({ method, url, headers, data }) {
  if (sensitive(url)) throw new Error("Sensitive requests cannot be queued offline");
  const safeHeaders = { ...(headers || {}) };
  delete safeHeaders.Authorization;
  delete safeHeaders.authorization;
  return tx("readwrite", (store) => new Promise((res) => {
    const req = store.add({
      method, url, headers: safeHeaders, data: data ?? null,
      ts: Date.now(),
      attempts: 0,
    });
    req.onsuccess = () => res(req.result);
  }));
}

export async function listPending() {
  return tx("readonly", (store) => new Promise((res) => {
    const req = store.getAll();
    req.onsuccess = () => res(req.result || []);
  }));
}

export async function countPending() {
  return tx("readonly", (store) => new Promise((res) => {
    const req = store.count();
    req.onsuccess = () => res(req.result || 0);
  }));
}

export async function deletePending(id) {
  return tx("readwrite", (store) => new Promise((res) => {
    const req = store.delete(id);
    req.onsuccess = () => res(true);
  }));
}

export async function clearAll() {
  return tx("readwrite", (store) => new Promise((res) => {
    const req = store.clear();
    req.onsuccess = () => res(true);
  }));
}

// Max retries per pending item before we drop it. Prevents the queue from
// looping forever on stale URLs (e.g. items captured under an old preview
// backend URL that the production frontend can no longer reach due to CORS).
const MAX_ATTEMPTS = 5;

/**
 * Rewrite a queued URL onto the CURRENT backend origin if it points to a
 * different origin. Without this, items captured under a preview URL keep
 * hitting that old origin after deploy → guaranteed CORS / 5xx forever.
 */
function rewriteOrigin(url) {
  try {
    const current = window.location.hostname.endsWith(".preview.emergentagent.com")
      ? window.location.origin : process.env.REACT_APP_BACKEND_URL;
    if (!current) return url;
    const u = new URL(url);
    const cur = new URL(current);
    if (u.origin === cur.origin) return url;
    // Replace just the origin; keep path/query/hash.
    return `${cur.origin}${u.pathname}${u.search}${u.hash}`;
  } catch {
    return url;
  }
}

/**
 * Drain the queue: replay each pending request via fetch.
 * On success → delete from store. On failure → keep + bump attempts.
 * Returns { synced, failed }.
 */
export async function drainQueue() {
  const items = await listPending();
  const token = localStorage.getItem("gio_token");
  let synced = 0, failed = 0;
  for (const item of items) {
    if (sensitive(item.url)) {
      await deletePending(item.id);
      continue;
    }
    const attempts = (item.attempts || 0) + 1;
    if (attempts > MAX_ATTEMPTS) {
      // eslint-disable-next-line no-console
      console.warn("[offline-queue] dropping after max attempts", item.url);
      await deletePending(item.id);
      failed++;
      continue;
    }
    try {
      const headers = { ...(item.headers || {}), "Content-Type": "application/json" };
      // Always re-attach current auth token (stored token in headers may be stale or stripped)
      if (token) headers.Authorization = `Bearer ${token}`;
      // Always rewrite the URL onto the CURRENT backend origin (fixes stale
      // origins captured by an older build).
      const url = rewriteOrigin(item.url);
      const res = await fetch(url, {
        method: item.method,
        headers,
        body: item.data ? JSON.stringify(item.data) : undefined,
      });
      if (res.ok || res.status === 409) {
        await deletePending(item.id);
        synced++;
      } else if (res.status === 401 || res.status === 403) {
        // auth issue → keep, but still bump attempts so it doesn't loop forever
        await tx("readwrite", (store) => new Promise((res2) => {
          item.attempts = attempts;
          const r = store.put(item);
          r.onsuccess = () => res2(true);
        }));
        failed++;
      } else if (res.status >= 400 && res.status < 500) {
        // permanent client error — drop so we don't loop forever
        // eslint-disable-next-line no-console
        console.warn("[offline-queue] dropping 4xx", url, res.status);
        await deletePending(item.id);
        failed++;
      } else {
        // 5xx → keep and bump attempts
        await tx("readwrite", (store) => new Promise((res2) => {
          item.attempts = attempts;
          const r = store.put(item);
          r.onsuccess = () => res2(true);
        }));
        failed++;
      }
    } catch {
      // Network error (CORS, DNS, etc.) → bump attempts, don't loop forever
      await tx("readwrite", (store) => new Promise((res2) => {
        item.attempts = attempts;
        const r = store.put(item);
        r.onsuccess = () => res2(true);
      }));
      failed++;
    }
  }
  return { synced, failed };
}

/** Subscribe to queue changes (poll every 2s while listener attached). */
export function subscribeCount(cb) {
  let active = true;
  let last = -1;
  const tick = async () => {
    if (!active) return;
    try {
      const c = await countPending();
      if (c !== last) { last = c; cb(c); }
    } catch { /* noop */ }
    setTimeout(tick, 2000);
  };
  tick();
  return () => { active = false; };
}
